import { motion } from "framer-motion";
import {
  Tv,
  WashingMachine,
  Music,
  Microwave,
  Laptop,
  Monitor,
  Wifi,
  Database,
  Star,
  ShieldCheck,
  Clock,
  Phone,
  CheckCircle2,
  Calendar,
  Quote,
  User
} from "lucide-react";
import { Link } from "react-router-dom";
import { Service, Technician, Testimonial, ROUTE_PATHS } from "@/lib/index";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const IconMap: Record<string, any> = {
  Tv,
  WashingMachine,
  Music,
  Microwave,
  Laptop,
  Monitor,
  Wifi,
  Database,
};

export function ServiceCard({ service }: { service: Service }) {
  const IconComponent = IconMap[service.iconName] || Monitor;

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="h-full"
    >
      <Card className="h-full flex flex-col border-border/50 bg-card hover:shadow-xl transition-all duration-300">
        <CardHeader className="space-y-4">
          <div className="flex justify-between items-start">
            <div className="p-3 rounded-xl bg-primary/10 text-primary">
              <IconComponent size={28} />
            </div>
            <Badge variant={service.category === "electronics" ? "secondary" : "outline"} className="capitalize">
              {service.category.replace("-", " ")}
            </Badge>
          </div>
          <div>
            <CardTitle className="text-xl font-bold">{service.title}</CardTitle>
            <CardDescription className="mt-2 line-clamp-2">
              {service.description}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex-grow space-y-4">
          <div className="flex flex-wrap gap-2">
            {service.features.slice(0, 3).map((feature, idx) => (
              <div key={idx} className="flex items-center text-xs text-muted-foreground">
                <CheckCircle2 size={12} className="mr-1 text-primary" />
                {feature}
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-border/50">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Estimated Price:</span>
              <span className="font-semibold text-foreground">{service.priceRange}</span>
            </div>
            <div className="flex gap-2 mt-3">
              {service.onSiteAvailable && (
                <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[10px] uppercase tracking-wider">
                  On-Site
                </Badge>
              )}
              {service.onCallAvailable && (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[10px] uppercase tracking-wider">
                  Remote/Call
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button asChild className="w-full group" variant="secondary">
            <Link to={ROUTE_PATHS.CONTACT}>
              Book Repair
              <Clock size={16} className="ml-2 group-hover:rotate-12 transition-transform" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export function TechnicianCard({ technician }: { technician: Technician }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
    >
      <Card className="overflow-hidden border-border/50 bg-card">
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={technician.image}
            alt={technician.name}
            className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500 scale-105 hover:scale-100"
          />
        </div>
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-lg font-bold">{technician.name}</h3>
              <p className="text-sm text-primary font-medium">{technician.role}</p>
            </div>
            <div className="flex items-center bg-accent px-2 py-1 rounded-md">
              <Star size={14} className="text-yellow-500 fill-yellow-500 mr-1" />
              <span className="text-sm font-bold">{technician.rating}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {technician.experienceYears}+ Years Experience
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {technician.specialization.map((spec, idx) => (
              <Badge key={idx} variant="secondary" className="text-[10px] bg-muted">
                {spec}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-4 pt-4 border-t border-border/50">
            <div className="flex items-center text-xs text-muted-foreground">
              <ShieldCheck size={14} className="mr-1 text-primary" />
              Certified Expert
            </div>
            <div className="flex items-center text-xs text-muted-foreground">
              <User size={14} className="mr-1 text-primary" />
              {technician.reviewsCount} Reviews
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <Card className="relative bg-card border-border/50 p-6 flex flex-col h-full">
      <div className="absolute top-6 right-6 text-primary/10">
        <Quote size={48} />
      </div>
      <div className="flex items-center gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < testimonial.rating ? "text-yellow-500 fill-yellow-500" : "text-muted"}
          />
        ))}
      </div>
      <blockquote className="flex-grow">
        <p className="text-base text-foreground/90 italic leading-relaxed">
          "{testimonial.content}"
        </p>
      </blockquote>
      <div className="mt-6 flex items-center gap-4">
        <Avatar className="h-12 w-12 border-2 border-primary/20">
          <AvatarImage src={testimonial.image} alt={testimonial.author} />
          <AvatarFallback>{testimonial.author[0]}</AvatarFallback>
        </Avatar>
        <div>
          <h4 className="font-bold text-sm">{testimonial.author}</h4>
          <p className="text-xs text-muted-foreground">{testimonial.role}</p>
        </div>
        <div className="ml-auto flex flex-col items-end">
          <div className="flex items-center text-[10px] text-muted-foreground">
            <Calendar size={10} className="mr-1" />
            {new Date(testimonial.date).toLocaleDateString()}
          </div>
          <Badge variant="outline" className="text-[10px] mt-1">
            Verified Customer
          </Badge>
        </div>
      </div>
    </Card>
  );
}
